import { useState } from "react";
import {
  change,
  clearFields,
  Field,
  getFormValues,
  SubmissionError,
} from "redux-form";

import {
  Alert,
  Box,
  Button,
  IconBackground,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { MFAMethodSelection } from "@js/apps/common/forms/fields/otp/mfa-method-selection";
import { OTPAuthCodeField } from "@js/apps/common/forms/fields/otp/otp-auth-code";
import {
  MODAL_MODE,
  PasskeyStepUpSubmit,
  useInitialMFAMode,
} from "@js/apps/common/forms/fields/otp/otp-auth-modal";
import { RouterLink } from "@js/components/link";
import { ModalConfirm, ModalInstance } from "@js/components/modal";
import { CheckboxField } from "@js/forms/fields/checkbox";
import { useAppDispatch, useAppSelector } from "@js/hooks";

import type { WithdrawBalancePayload } from "../../api";
import { useWithdrawBalanceMutation } from "../../api";

import {
  WITHDRAWAL_FORM_ID,
  WithdrawalBTRSTFormContent,
  WithdrawalBTRSTFormInstance,
} from "./form";
import { WithdrawalModalInstance } from "./modal-instance";

const modalAlertContentSx = { width: "100%", padding: "16px" } as const;
const modalAlertIconSx = { marginRight: "1.5rem" } as const;

export const WithdrawalModalContent = () => {
  const [show2FA, setShow2FA] = useState(false);
  const [withdrawBalance] = useWithdrawBalanceMutation();
  const dispatch = useAppDispatch();
  const { mode, setMode, bothAvailable } = useInitialMFAMode();
  const [autoStartPasskey, setAutoStartPasskey] = useState(false);
  const formValues = useAppSelector(
    (state) =>
      getFormValues(WITHDRAWAL_FORM_ID)(state) as
        Partial<WithdrawBalancePayload> | undefined,
  );

  const onSubmit = async (values: WithdrawBalancePayload) => {
    if (!values.code && !values.key) {
      dispatch(clearFields(WITHDRAWAL_FORM_ID, false, false, "is_backup_code"));
    }

    return withdrawBalance(values)
      .unwrap()
      .then(() => {
        if (values.code || values.key) {
          WithdrawalModalInstance.close();
          openFinalizeWithdrawalModal();
        } else {
          setShow2FA(true);
        }
      })
      .catch((error) => {
        throw new SubmissionError(error?.data);
      });
  };

  // The passkey ceremony doesn't run inside the redux-form's own submit cycle - it
  // authenticates independently and then calls this directly with the resulting key,
  // merged with the amount/wallet_address already captured in step 1.
  const handlePasskeySubmit = ({ key }: { key?: string }) =>
    onSubmit({ ...formValues, key } as WithdrawBalancePayload);

  const resetMFAField = () => {
    dispatch(change(WITHDRAWAL_FORM_ID, "code", undefined));
  };

  const openFinalizeWithdrawalModal = () =>
    ModalInstance.open({
      className: "max-width-500",
      children: (
        <>
          <ModalConfirm onConfirm={ModalInstance.close} confirmText="Close">
            <Typography
              component="h4"
              variant="title"
              fontWeight={400}
              sx={{ mb: 2 }}
            >
              To continue, click the link in the verification email sent to you.
            </Typography>
            <Typography component="p" sx={{ mb: 2 }}>
              For security reasons, you need to verify your transfer via email.
              You need to click the link in the email sent to you, then finalize
              the transfer back on Braintrust.
              <strong>
                {" "}
                If you skip this step, your tokens will not be transferred.
              </strong>
            </Typography>
            <Typography component="p" sx={{ mb: 4 }}>
              The link expires in 30 minutes.
            </Typography>
          </ModalConfirm>
        </>
      ),
    });

  const onCancelMFAStep = () => {
    resetMFAField();
    if (bothAvailable) {
      setMode(MODAL_MODE.SELECT);
    } else {
      setShow2FA(false);
    }
  };

  const renderMFAStep = () => {
    if (mode === MODAL_MODE.SELECT) {
      return (
        <MFAMethodSelection
          onSelectOtp={() => setMode(MODAL_MODE.CODE)}
          onSelectPasskey={() => {
            setAutoStartPasskey(true);
            setMode(MODAL_MODE.PASSKEY);
          }}
          onCancel={() => {
            resetMFAField();
            setShow2FA(false);
          }}
        />
      );
    }

    if (mode === MODAL_MODE.PASSKEY) {
      return (
        <PasskeyStepUpSubmit
          onCancel={onCancelMFAStep}
          cancelLabel={bothAvailable ? "Use different method" : "Cancel"}
          onSubmit={handlePasskeySubmit}
          autoStart={autoStartPasskey}
        />
      );
    }

    return (
      <>
        <Typography
          component="h4"
          variant="title"
          fontWeight={400}
          sx={{ mb: 2 }}
        >
          2-step authentication
        </Typography>
        <Typography component="p" sx={{ mb: 4 }}>
          Enter the 6-digit code generated by your authenticator app.
        </Typography>
        <OTPAuthCodeField name="code" />
        <Field
          name="is_backup_code"
          component={CheckboxField}
          label="Use backup code instead"
        />
        <Box sx={{ mt: 4 }}>
          <Button variant="secondary" size="medium" onClick={onCancelMFAStep}>
            {bothAvailable ? "Use different method" : "Back"}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <WithdrawalBTRSTFormInstance onSubmit={onSubmit}>
      {show2FA ? (
        renderMFAStep()
      ) : (
        <>
          <Typography
            component="h2"
            variant="title"
            fontWeight={400}
            size="medium"
            sx={{ pt: 4, pb: 4 }}
          >
            Transfer BTRST tokens to a wallet on the Base network
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Alert
              contentSx={modalAlertContentSx}
              iconSx={modalAlertIconSx}
              type="warning-dark"
              alignIcon="center"
              rounded
              withIcon
              customIcon={
                <IconBackground
                  size="large"
                  backgroundColor="var(--soft-orange)"
                  sx={{ fontSize: "1.25rem" }}
                >
                  🚨
                </IconBackground>
              }
            >
              <Typography component="p" variant="paragraph" size="medium">
                Your wallet is now on the Base blockchain, so you must choose a{" "}
                <Typography
                  component="link"
                  href={SETTINGS.SUPPORT_URLS.HOW_TO_WITHDRAW_TOKEN_ARTICLE_URL}
                  target="_blank"
                  RouterLink={RouterLink}
                >
                  Base-compatible wallet
                </Typography>
                .
              </Typography>
            </Alert>
            <Alert
              contentSx={modalAlertContentSx}
              iconSx={modalAlertIconSx}
              type="warning-dark"
              alignIcon="center"
              rounded
              withIcon
              customIcon={
                <IconBackground
                  size="large"
                  backgroundColor="var(--soft-orange)"
                  sx={{ fontSize: "1.25rem" }}
                >
                  ⏰
                </IconBackground>
              }
            >
              <Typography component="p" variant="paragraph" size="medium">
                Transfers may take up to 24 hours to process.
              </Typography>
            </Alert>
          </Box>
          <WithdrawalBTRSTFormContent
            onCancel={WithdrawalModalInstance.close}
          />
        </>
      )}
    </WithdrawalBTRSTFormInstance>
  );
};
